import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Snowflake, Sun, Calendar, User, Package, AlertTriangle, ChevronRight, ArrowRight, ArrowLeft, ArrowUpRight, Camera, Zap, FileText, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL;
if (!PUBLIC_BASE) throw new Error("REACT_APP_BACKEND_URL non configurato");

const formatDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("T")[0].split("-");
  return `${d}/${m}/${y}`;
};

/** Risolve path immagine relativo → URL assoluto */
const resolveImg = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${PUBLIC_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
};

/**
 * Visualizzatore full-screen con swipe orizzontale nativo (CSS scroll-snap).
 * Fluido su mobile senza librerie: overflow-x-auto + snap-x mandatory,
 * ogni slide occupa 100vw. Swipe con dita = scroll nativo del browser.
 */
const SwipeGallery = ({ slides, initialIndex = 0, open, onClose }) => {
  const scrollerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (!open) return;
    setCurrentIndex(initialIndex);
    requestAnimationFrame(() => {
      const el = scrollerRef.current;
      if (el) el.scrollTo({ left: initialIndex * el.clientWidth, behavior: "instant" });
    });
  }, [open, initialIndex]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && scrollerRef.current) {
        scrollerRef.current.scrollBy({ left: scrollerRef.current.clientWidth, behavior: "smooth" });
      }
      if (e.key === "ArrowLeft" && scrollerRef.current) {
        scrollerRef.current.scrollBy({ left: -scrollerRef.current.clientWidth, behavior: "smooth" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const onScrollNative = (e) => {
    const el = e.currentTarget;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== currentIndex && idx >= 0 && idx < slides.length) {
      setCurrentIndex(idx);
    }
  };

  if (!open || !slides || slides.length === 0) return null;
  const currentLabel = slides[currentIndex]?.label;
  const hasMultiple = slides.length > 1;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/95 flex flex-col select-none" data-testid="swipe-gallery">
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center gap-2 min-w-0">
          {currentLabel && (
            <span className="text-white text-xs font-semibold uppercase tracking-wide truncate max-w-[60vw]">{currentLabel}</span>
          )}
          {hasMultiple && (
            <span className="text-white/70 text-xs font-mono">{currentIndex + 1} / {slides.length}</span>
          )}
        </div>
        <button type="button" onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 backdrop-blur text-white flex items-center justify-center active:scale-90 transition-transform"
          aria-label="Chiudi" data-testid="gallery-close-btn">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div ref={scrollerRef} onScroll={onScrollNative}
        className="flex-1 overflow-x-auto overflow-y-hidden snap-x snap-mandatory flex touch-pan-x scrollbar-none"
        style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
        data-testid="gallery-scroller">
        {slides.map((s, i) => (
          <div key={i} className="snap-center flex-shrink-0 w-screen h-full flex items-center justify-center p-4"
            data-testid={`gallery-slide-${i}`}>
            <img src={s.src} alt={s.alt || `Foto ${i + 1}`} data-testid={`gallery-image-${i}`}
              className="max-w-full max-h-full object-contain rounded-xl" draggable={false} />
          </div>
        ))}
      </div>

      {hasMultiple && (
        <div className="absolute bottom-6 inset-x-0 flex items-center justify-center gap-2 z-10" data-testid="gallery-dots">
          {slides.map((_, i) => (
            <button key={i} type="button"
              onClick={() => { const el = scrollerRef.current; if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" }); }}
              className={`h-2 rounded-full transition-all ${i === currentIndex ? "w-6 bg-white" : "w-2 bg-white/40"}`}
              aria-label={`Vai a foto ${i + 1}`} />
          ))}
        </div>
      )}

      {hasMultiple && currentIndex === 0 && (
        <div className="absolute bottom-16 right-4 z-10 text-white/70 text-[11px] font-medium bg-black/40 backdrop-blur px-2.5 py-1 rounded-full flex items-center gap-1 pointer-events-none animate-pulse">
          Scorri <ChevronRight className="h-3.5 w-3.5" />
        </div>
      )}
    </div>
  );
};


/** Calcola giorni rimanenti alla scadenza (negativo = scaduto) */
const giorniAllaScadenza = (iso) => {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(iso);
  exp.setHours(0, 0, 0, 0);
  return Math.round((exp - today) / (1000 * 60 * 60 * 24));
};

const ScadenzaBadge = ({ data_scadenza }) => {
  if (!data_scadenza) return null;
  const gg = giorniAllaScadenza(data_scadenza);
  let color = "bg-emerald-100 text-emerald-800 border-emerald-300";
  let icon = "✓";
  if (gg !== null) {
    if (gg < 0) { color = "bg-red-100 text-red-800 border-red-300"; icon = "⚠️"; }
    else if (gg <= 3) { color = "bg-amber-100 text-amber-800 border-amber-300"; icon = "⏰"; }
  }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold ${color}`}>
      <span>{icon}</span>
      <span>Scad. {formatDate(data_scadenza)}</span>
      {gg !== null && gg >= 0 && <span className="opacity-70">({gg}g)</span>}
      {gg !== null && gg < 0 && <span className="opacity-70">(scaduto)</span>}
    </span>
  );
};

const StatoBadge = ({ stato, metodoConservazione, rowId }) => {
  if (stato === "fresco" && metodoConservazione === "temp_ambiente") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300" data-testid={`ingredient-status-${rowId}`}>Temp. ambiente</span>;
  }
  if (!stato || stato === "fresco") return null;
  if (stato === "consumato") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-300">
        <Package className="h-3 w-3" /> Consumato
      </span>
    );
  }
  if (stato === "congelato") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-100 text-sky-800 border border-sky-300">
        <Snowflake className="h-3 w-3" /> Congelato
      </span>
    );
  }
  if (stato === "scongelato") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-800 border border-orange-300">
        <Sun className="h-3 w-3" /> Scongelato
      </span>
    );
  }
  return null;
};

/**
 * Riga ingrediente con info sempre visibili (lotto, scadenza, foto thumbnail).
 * Tap sulla foto thumbnail → chiama onOpenGallery con l'indice della slide.
 * Se l'ingrediente ha una fattura, mostra un badge "Fattura" cliccabile.
 */
const IngredienteRow = ({ nome, quantita, unita_misura, lotto_numero, data_scadenza, stato, metodoConservazione, rowId, data_congelamento, immagine_path, onOpenGallery, slideIndex, fatturaSlideIndex, hasFattura, lottoOrigineId, lottoOrigineUrl, quantitaUtilizzata, unitaUtilizzo, quantitaPrima, quantitaDopo, unitaOrigine, dataCreazioneOrigine, haOriginiPrecedenti }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const imgUrl = resolveImg(immagine_path);
  const showImg = imgUrl && !imgFailed;

  return (
    <div className="py-3 border-b border-slate-100 last:border-0 first:pt-1">
      <div className="flex items-start gap-3">
        {/* Thumbnail foto cliccabile (left) */}
        {showImg ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenGallery?.(slideIndex); }}
            className="w-14 h-14 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0 relative group"
            data-testid="open-photo-btn"
          >
            <img
              src={imgUrl}
              alt={nome}
              className="w-full h-full object-cover"
              onError={() => setImgFailed(true)}
            />
            <span className="absolute bottom-0 right-0 bg-black/60 text-white p-0.5 rounded-tl-md">
              <Camera className="h-2.5 w-2.5" />
            </span>
          </button>
        ) : (
          <div className="w-14 h-14 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
            <Package className="h-5 w-5 text-slate-300" />
          </div>
        )}

        {/* Info principali */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="text-sm font-bold text-slate-900 truncate">{nome || "—"}</p>
            {quantita && (
              <span className="text-xs font-bold text-slate-700 flex-shrink-0">
                {quantita}{unita_misura ? ` ${unita_misura}` : ""}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            {lotto_numero && (
              <span className="text-[10px] text-slate-500 font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                Lotto {lotto_numero}
              </span>
            )}
            <StatoBadge stato={stato} metodoConservazione={metodoConservazione} rowId={rowId} />
            <ScadenzaBadge data_scadenza={data_scadenza} />
            {hasFattura && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenGallery?.(fatturaSlideIndex); }}
                className="text-[10px] text-indigo-700 bg-indigo-100 hover:bg-indigo-200 border border-indigo-200 px-1.5 py-0.5 rounded font-bold flex items-center gap-1 transition-colors"
                data-testid="open-fattura-ing-btn"
              >
                <FileText className="h-2.5 w-2.5" /> Fattura
              </button>
            )}
          </div>

          {stato === "congelato" && data_congelamento && (
            <p className="text-[10px] text-sky-700 flex items-center gap-1 mt-1">
              <Snowflake className="h-2.5 w-2.5" /> Congelato il {formatDate(data_congelamento)}
            </p>
          )}
          {lottoOrigineId && (
            <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50/70 p-2.5" data-testid={`mother-lot-details-${lottoOrigineId}`}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Lotto madre selezionato</p>
              <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-emerald-900">
                {dataCreazioneOrigine && <><span>Creazione</span><strong>{formatDate(dataCreazioneOrigine)}</strong></>}
                {quantitaUtilizzata != null && <><span>Quantità prelevata</span><strong>{quantitaUtilizzata} {unitaUtilizzo || ""}</strong></>}
                {quantitaPrima != null && <><span>Prima del prelievo</span><strong>{quantitaPrima} {unitaOrigine || ""}</strong></>}
                {quantitaDopo != null && <><span>Dopo il prelievo</span><strong>{quantitaDopo} {unitaOrigine || ""}</strong></>}
              </div>
              <a
                href={lottoOrigineUrl || `/lotti/p/${lottoOrigineId}`}
                className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-emerald-800 underline underline-offset-2"
                data-testid={`mother-lot-trace-link-${lottoOrigineId}`}
              >
                {haOriginiPrecedenti ? "Apri l’intera catena di origine" : "Apri tracciabilità lotto madre"}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Pagina PUBBLICA (no auth) accessibile via QR Code stampato sull'etichetta.
 * Mostra composizione lotto + storico stati HACCP per tracciabilità completa.
 */
/**
 * Foto principale del lotto (data.immagine_path) — thumb cliccabile.
 * Prima card visibile per lotti singoli (non composti).
 */
const FotoLottoCard = ({ path, onOpenGallery, slideIndex }) => {
  const [failed, setFailed] = useState(false);
  const src = resolveImg(path);
  if (!src || failed) return null;
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="foto-lotto-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Camera className="h-4 w-4 text-emerald-500" />
          <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Foto Lotto</p>
        </div>
        <span className="text-[9px] text-slate-400 italic">Tocca per ingrandire</span>
      </div>
      <button
        type="button"
        onClick={() => onOpenGallery?.(slideIndex)}
        className="w-full rounded-xl overflow-hidden bg-slate-100 border border-slate-200 active:scale-[0.99] transition-transform"
        data-testid="foto-lotto-thumb"
      >
        <img
          src={src}
          alt="Foto lotto"
          className="w-full max-h-80 object-cover"
          onError={() => setFailed(true)}
        />
      </button>
    </div>
  );
};

/**
 * Fattura del lotto (foto singola) — thumb cliccabile, delega apertura al parent.
 */
const FatturaLottoCard = ({ path, onOpenGallery, slideIndex }) => {
  const [failed, setFailed] = useState(false);
  const src = resolveImg(path);
  if (!src || failed) return null;
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="fattura-lotto-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-indigo-500" />
          <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Fattura Fornitore</p>
        </div>
        <span className="text-[9px] text-slate-400 italic">Tocca per ingrandire</span>
      </div>
      <button
        type="button"
        onClick={() => onOpenGallery?.(slideIndex)}
        className="w-full rounded-xl overflow-hidden bg-slate-100 border border-slate-200 active:scale-[0.99] transition-transform"
        data-testid="fattura-lotto-thumb"
      >
        <img
          src={src}
          alt="Fattura fornitore"
          data-testid="fattura-lotto-image"
          className="w-full max-h-64 object-cover"
          onError={() => setFailed(true)}
        />
      </button>
    </div>
  );
};

const LottoPubblico = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const handleClose = () => {
    // Se apribile nella history dell'app → torna indietro (resta nella PWA);
    // altrimenti (es. scansione QR diretta) prova a chiudere la tab o va alla home.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      try { window.close(); } catch { /* noop */ }
      if (!window.closed) window.location.href = "/";
    }
  };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/lotti/${id}/pubblico`);
        if (mounted) setData(res.data);
      } catch (err) {
        if (mounted) setError(err.response?.status === 404 ? "Lotto non trovato" : "Errore di caricamento");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  // Costruisce le slides della gallery:
  //  1. Foto principale del lotto (data.immagine_path) — sempre prima se presente
  //  2. Fattura fornitore (data.fattura_immagine_path)
  //  3. Foto ingredienti di composizione / lotti_origine (in ordine)
  // Ogni slide ha sourceId per il lookup dell'indice.
  const gallerySlides = (() => {
    if (!data) return [];
    const slides = [];
    // Foto lotto (principale)
    if (data.immagine_path) {
      const src = resolveImg(data.immagine_path);
      if (src) slides.push({ src, alt: "Foto lotto", label: "Foto Lotto", sourceId: "lotto" });
    }
    // Fattura
    if (data.fattura_immagine_path) {
      const src = resolveImg(data.fattura_immagine_path);
      if (src) slides.push({ src, alt: "Fattura fornitore", label: "Fattura Fornitore", sourceId: "fattura" });
    }
    // Composizione / origine — foto ingrediente + fattura (se presente)
    const composizione = data.composizione || [];
    const origine = data.lotti_origine || [];
    if (composizione.length > 0) {
      composizione.forEach((c, i) => {
        const src = resolveImg(c.immagine_path);
        if (src) slides.push({ src, alt: c.ingrediente_nome || "Ingrediente", label: c.ingrediente_nome || "Ingrediente", sourceId: `comp-${i}` });
        // Fattura ingrediente (facoltativa)
        const fSrc = resolveImg(c.fattura_immagine_path);
        if (fSrc) slides.push({
          src: fSrc,
          alt: `Fattura ${c.ingrediente_nome || "ingrediente"}`,
          label: `Fattura · ${c.ingrediente_nome || "ingrediente"}`,
          sourceId: `comp-fattura-${i}`,
        });
      });
    } else {
      origine.forEach((l, i) => {
        const src = resolveImg(l.immagine_path);
        if (src) slides.push({ src, alt: l.ingrediente_nome || "Ingrediente", label: l.ingrediente_nome || "Ingrediente", sourceId: `lo-${i}` });
      });
    }
    return slides;
  })();

  const slideIndexFor = (sourceId) => gallerySlides.findIndex(s => s.sourceId === sourceId);

  const openGalleryAt = (idx) => {
    if (idx == null || idx < 0) return;
    setGalleryIndex(idx);
    setGalleryOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-amber-500 mb-3" />
        <h1 className="text-xl font-bold text-slate-900 mb-1">{error || "Errore"}</h1>
        <p className="text-sm text-slate-500">ID lotto: <code className="text-xs">{id}</code></p>
      </div>
    );
  }

  const stato = data.stato || "fresco";
  const inAmbiente = stato === "fresco" && data.metodo_conservazione === "temp_ambiente";
  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-slate-50 to-slate-100" data-testid="lotto-public-page">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <button
              onClick={handleClose}
              className="flex items-center gap-1 -ml-1 px-2 py-1 rounded-lg text-slate-600 hover:bg-slate-100 active:scale-95 transition"
              data-testid="lotto-pubblico-close"
              aria-label="Chiudi"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-xs font-semibold">Chiudi</span>
            </button>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${stato === "congelato" ? "bg-sky-100 text-sky-700" : stato === "scongelato" ? "bg-orange-100 text-orange-700" : stato === "consumato" ? "bg-slate-200 text-slate-700" : inAmbiente ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"}`} data-testid="lotto-stato">
              {stato === "congelato" ? <><Snowflake className="h-2.5 w-2.5" /> CONGELATO</>
                : stato === "scongelato" ? <><Sun className="h-2.5 w-2.5" /> SCONGELATO</>
                : stato === "consumato" ? <><Package className="h-2.5 w-2.5" /> CONSUMATO</>
                : <>{inAmbiente ? "TEMP. AMBIENTE" : "FRIGO"}</>}
            </span>
          </div>
          <p className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-1">Tracciabilità Lotto</p>
          <h1 className="text-xl font-bold text-slate-900" data-testid="lotto-nome">
            {data.ricetta_nome || data.ingrediente_nome || "—"}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Lotto N° <span className="font-mono font-bold text-slate-700">{data.numero_lotto || "—"}</span>
            {data.quantita_preparata != null
              ? <> · <span className="font-semibold text-slate-700">{data.quantita_preparata}{data.quantita_iniziale != null && Number(data.quantita_iniziale) !== Number(data.quantita_preparata) ? `/${data.quantita_iniziale}` : ""}{data.unita_misura ? ` ${data.unita_misura}` : ""}</span>{data.quantita_iniziale != null && Number(data.quantita_iniziale) !== Number(data.quantita_preparata) ? " (rimanente/iniziale)" : ""}</>
              : (data.quantita_testo && <> · {data.quantita_testo}</>)}
          </p>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-4 space-y-3">
        {/* Date card */}
        <div className="bg-white rounded-2xl p-4 shadow-sm space-y-2">
          <div className="flex items-start gap-3">
            <Calendar className="h-4 w-4 text-slate-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Date</p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-1 text-sm">
                <span className="text-slate-500">Creazione</span><span className="font-semibold text-slate-900" data-testid="data-creazione">{formatDate(data.data_creazione)}</span>
                {data.data_congelamento && (<><span className="text-sky-600">❄️ Congelato</span><span className="font-semibold text-sky-700">{formatDate(data.data_congelamento)}</span></>)}
                {data.data_scongelamento && (<><span className="text-orange-600">☀️ Scongelato</span><span className="font-semibold text-orange-700">{formatDate(data.data_scongelamento)}</span></>)}
                <span className="text-slate-500 font-bold">Scadenza</span><span className="font-bold text-slate-900" data-testid="data-scadenza">{formatDate(data.data_scadenza)}</span>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-2 flex items-center gap-2 text-xs text-slate-500">
            <User className="h-3.5 w-3.5" />
            <span>Operatore: <strong className="text-slate-700">{data.operatore || "—"}</strong></span>
          </div>
        </div>

        {/* ════ Allergeni HACCP ════ */}
        {data.allergeni_calcolabile && (
          <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="allergeni-card">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-rose-500" />
                <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Allergeni HACCP</p>
              </div>
              <span className="text-[9px] text-slate-400 italic">Reg. UE 1169/2011</span>
            </div>
            {data.allergeni_dettagli?.length > 0 ? (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {data.allergeni_dettagli.map((a) => (
                    <span
                      key={a.key}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-800 text-xs font-semibold"
                    >
                      <span>{a.icon}</span>
                      <span>{a.label}</span>
                    </span>
                  ))}
                </div>
                {data.allergeni_non_verificati > 0 && (
                  <p className="text-[10px] text-amber-700 mt-2.5 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {data.allergeni_non_verificati} ingredient{data.allergeni_non_verificati === 1 ? "e" : "i"} con allergeni in fase di verifica
                  </p>
                )}
                <p className="text-[10px] text-slate-400 italic mt-2">
                  In caso di intolleranze o allergie, chiedere conferma allo staff prima del consumo.
                </p>
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2 border border-emerald-100">
                <span className="text-base">✓</span>
                <span className="font-semibold">Nessun allergene rilevato</span>
              </div>
            )}
          </div>
        )}

        {/* Foto del lotto (opzionale) — prima card, cliccabile → gallery */}
        {data.immagine_path && (
          <FotoLottoCard
            path={data.immagine_path}
            onOpenGallery={openGalleryAt}
            slideIndex={slideIndexFor("lotto")}
          />
        )}

        {/* Fattura fornitore (opzionale) */}
        {data.fattura_immagine_path && (
          <FatturaLottoCard
            path={data.fattura_immagine_path}
            onOpenGallery={openGalleryAt}
            slideIndex={slideIndexFor("fattura")}
          />
        )}

        {/* Composizione — ingredienti con scadenza + foto espandibili */}
        {(data.composizione?.length > 0 || data.lotti_origine?.length > 0) && (
          <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="composizione-card">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-slate-400" />
                <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Composizione</p>
              </div>
            </div>
            <div className="space-y-0">
              {data.composizione?.map((c, i) => (
                <IngredienteRow
                  key={`comp-${i}`}
                  nome={c.ingrediente_nome}
                  quantita={c.quantita}
                  unita_misura={c.unita_misura}
                  lotto_numero={c.lotto_origine_numero}
                  data_scadenza={c.data_scadenza}
                  stato={c.stato}
                  metodoConservazione={c.metodo_conservazione}
                  rowId={`comp-${i}`}
                  data_congelamento={c.data_congelamento}
                  immagine_path={c.immagine_path}
                  onOpenGallery={openGalleryAt}
                  slideIndex={slideIndexFor(`comp-${i}`)}
                  hasFattura={!!c.fattura_immagine_path}
                  fatturaSlideIndex={slideIndexFor(`comp-fattura-${i}`)}
                  lottoOrigineId={c.lotto_origine_id}
                  lottoOrigineUrl={c.lotto_origine_url}
                  quantitaUtilizzata={c.quantita_utilizzata}
                  unitaUtilizzo={c.unita_misura_utilizzo}
                  quantitaPrima={c.lotto_origine_quantita_prima}
                  quantitaDopo={c.lotto_origine_quantita_dopo}
                  unitaOrigine={c.lotto_origine_unita_misura}
                  dataCreazioneOrigine={c.lotto_origine_data_creazione}
                  haOriginiPrecedenti={c.lotto_origine_ha_precedenti}
                />
              ))}
              {/* Fallback: solo lotti origine se composizione vuota */}
              {!data.composizione?.length && data.lotti_origine?.map((l, i) => (
                <IngredienteRow
                  key={`lo-${i}`}
                  nome={l.ingrediente_nome}
                  lotto_numero={l.numero_lotto}
                  data_scadenza={l.data_scadenza}
                  stato={l.stato}
                  metodoConservazione={l.metodo_conservazione}
                  rowId={`lo-${i}`}
                  data_congelamento={l.data_congelamento}
                  immagine_path={l.immagine_path}
                  onOpenGallery={openGalleryAt}
                  slideIndex={slideIndexFor(`lo-${i}`)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Storico stati HACCP */}
        {data.storico_stati?.length > 0 && (
          <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="storico-card">
            <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-3">Storico HACCP</p>
            <div className="space-y-2.5">
              {data.storico_stati
                .filter((s, i, arr) => {
                  const DISTINCT = ['split', 'split_figlio', 'scongelamento', 'prelievo_preparazione'];
                  if (DISTINCT.includes(s.azione)) return true; // eventi distinti sempre mostrati
                  const key = `${s.stato}|${s.data}`;
                  const sameKeyPlain = (x) => `${x.stato}|${x.data}` === key && !DISTINCT.includes(x.azione);
                  const withMetodoIdx = arr.findIndex((x) => sameKeyPlain(x) && x.metodo);
                  const keepIdx = withMetodoIdx !== -1 ? withMetodoIdx : arr.findIndex(sameKeyPlain);
                  return i === keepIdx; // una sola voce per stato+data, preferendo quella col metodo
                })
                .map((s, i) => {
                const isAbb = s.metodo === "abbattitore";
                const um = s.unita_misura || "";
                let labelStato, detail, dotColor, icon = null;
                if (s.azione === "split") {
                  dotColor = "bg-amber-500";
                  labelStato = `Scongelati ${s.quantita_scongelata ?? ""}${um ? " " + um : ""} → nuovo lotto`;
                  detail = `Da: ${s.operatore} · resta congelato${s.quantita_residua != null ? ` · ${s.quantita_residua}${um ? " " + um : ""} rimasti` : ""}`;
                } else if (s.azione === "spostamento") {
                  dotColor = "bg-sky-500";
                  labelStato = "Spostato in congelatore";
                  detail = `Da: ${s.operatore} · resta congelato`;
                } else if (s.azione === "split_figlio") {
                  dotColor = "bg-orange-500";
                  labelStato = "Scongelato (da lotto padre)";
                  detail = `Da: ${s.operatore} · scadenza ${formatDate(s.scadenza_pre)} → ${formatDate(s.scadenza_post)}`;
                } else if (s.azione === "prelievo_preparazione") {
                  dotColor = "bg-emerald-500";
                  labelStato = "Prelievo per preparazione";
                  detail = `${s.quantita_utilizzata ?? "—"} ${s.unita_misura || ""} · residuo ${s.quantita_residua ?? "—"} ${s.unita_misura_residua || ""} · Da: ${s.operatore || "—"}`;
                } else {
                  dotColor = s.stato === "congelato" ? (isAbb ? "bg-indigo-500" : "bg-sky-500") : "bg-orange-500";
                  labelStato = s.stato === "congelato"
                    ? (isAbb ? "Abbattuto e congelato" : "Congelato")
                    : (s.stato === "scongelato" ? "Scongelato"
                      : s.stato === "fresco" ? (s.metodo_conservazione === "temp_ambiente" ? "Temp. ambiente" : "Frigo") : s.stato);
                  detail = `Da: ${s.operatore} · scadenza ${formatDate(s.scadenza_pre)} → ${formatDate(s.scadenza_post)}`;
                  if (isAbb) icon = <Zap className="h-3 w-3 text-indigo-500" />;
                }
                return (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className={`mt-1 w-2 h-2 rounded-full ${dotColor} flex-shrink-0`}></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-slate-900 capitalize flex items-center gap-1">
                        {icon}
                        {labelStato} · {formatDate(s.data)}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        {detail}
                      </p>
                      {s.note && <p className="text-[11px] text-slate-400 italic mt-0.5">"{s.note}"</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ════ Yollo Verified — banner discreto inline ════ */}
        <a
          href="https://yollo.me/?utm_source=lotto_pubblico&utm_medium=qr&utm_campaign=verified_widget"
          target="_blank"
          rel="noopener noreferrer"
          className="group mt-4 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white shadow-sm border border-slate-200 text-sm text-slate-600 hover:text-slate-900 hover:border-indigo-200 hover:shadow-md transition-all"
          data-testid="yollo-verified-widget"
        >
          <img src="/yollo-logo.png" alt="Yollo" className="h-5 w-auto opacity-80 group-hover:opacity-100 transition-opacity" />
          <span>Tracciabilità HACCP · powered by <strong className="text-slate-900 group-hover:text-indigo-600">Yollo</strong></span>
          <ArrowRight className="h-4 w-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
        </a>
      </div>

      {/* Gallery a swipe (foto ingredienti + fattura) */}
      <SwipeGallery
        slides={gallerySlides}
        initialIndex={galleryIndex}
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
      />
    </div>
  );
};

export default LottoPubblico;
